import { memo, useCallback, useState } from "react";
import {
  DamagePinFormValues,
  DamageStatus,
  FleetVehicleDamageEditFormValues,
} from "services/api/fleet/vehicles/damages/types";
import { FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";
import FileUploadModal from "ui/components/FileUpload/FileUploadModal/FileUploadModal";
import DamagePinEditModal from "./DamagePinEditModal";
import DamagePhotoPanel from "./DamagePhotoPanel";
import DamagePhotoList from "./DamagePhotoList";
import DamagePinTable from "./DamagePinTable/DamagePinTable";
import {
  DAMAGE_STATUS_HEX,
  DEFAULT_DAMAGE_STATUS_HEX,
} from "./consts";

interface DamagePhotoPinFormProps {
  formValues: FleetVehicleDamageEditFormValues;
  setFormValues: React.Dispatch<
    React.SetStateAction<FleetVehicleDamageEditFormValues>
  >;
  statuses: DamageStatus[];
}

const DamagePhotoPinForm: React.FC<DamagePhotoPinFormProps> = ({
  formValues,
  setFormValues,
  statuses,
}) => {
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(
    formValues.photoUploads.length > 0 ? formValues.photoUploads[0].id : null
  );
  // Upload se pokreće s dva mjesta (gumb u popisu i prazan preglednik), pa modal
  // drži ovaj korak, a ne pojedina komponenta.
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [editingPin, setEditingPin] = useState<DamagePinFormValues | null>(
    null
  );
  const [activePin, setActivePin] = useState<string | null>(null);

  const handleUploadClick = useCallback(() => {
    setShowUploadModal(true);
  }, []);

  const handleUploadModalHide = useCallback(() => {
    setShowUploadModal(false);
  }, []);

  const handlePhotoUploadSave = useCallback(
    (uploads: FleetDocumentUpload[]) => {
      setFormValues((prev) => {
        const newPhotos = [...prev.photoUploads, ...uploads];
        if (!selectedPhotoId && newPhotos.length > 0) {
          setSelectedPhotoId(newPhotos[0].id);
        }
        return { ...prev, photoUploads: newPhotos };
      });

      setShowUploadModal(false);
    },
    [selectedPhotoId, setFormValues]
  );

  const handlePhotoDelete = (photoId: string) => {
    setFormValues((prev) => ({
      ...prev,
      photoUploads: prev.photoUploads.filter((p) => p.id !== photoId),
      pins: prev.pins.filter((p) => p.uploadId !== photoId),
    }));
    if (selectedPhotoId === photoId) {
      const remaining = formValues.photoUploads.filter(
        (p) => p.id !== photoId
      );
      setSelectedPhotoId(remaining.length > 0 ? remaining[0].id : null);
    }
  };

  const handleImageClick = (x: number, y: number) => {
    if (!selectedPhotoId) return;

    const newPin: DamagePinFormValues = {
      tempId: crypto.randomUUID(),
      vehicleDamageStatusId: statuses.length > 0 ? statuses[0].id : "",
      uploadId: selectedPhotoId,
      pinX: x,
      pinY: y,
      name: "",
      description: "",
    };
    setEditingPin(newPin);
    setShowPinModal(true);
  };

  const handlePinSave = (pin: DamagePinFormValues) => {
    setFormValues((prev) => {
      const existingIndex = prev.pins.findIndex(
        (p) => p.tempId === pin.tempId
      );
      if (existingIndex >= 0) {
        const updated = [...prev.pins];
        updated[existingIndex] = pin;
        return { ...prev, pins: updated };
      }
      return { ...prev, pins: [...prev.pins, pin] };
    });
    setShowPinModal(false);
    setEditingPin(null);
  };

  const handlePinSelect = (pin: DamagePinFormValues) => {
    setSelectedPhotoId(pin.uploadId);
    setActivePin(pin.tempId === activePin ? null : pin.tempId);
  };

  const handlePinEdit = (pin: DamagePinFormValues) => {
    setEditingPin(pin);
    setShowPinModal(true);
    setSelectedPhotoId(pin.uploadId);
    setActivePin(pin.tempId);
  };

  const handlePinDelete = (tempId: string) => {
    setFormValues((prev) => ({
      ...prev,
      pins: prev.pins.filter((p) => p.tempId !== tempId),
    }));
  };

  const handlePinClick = (pinId: string) => {
    setActivePin(pinId === activePin ? null : pinId);
  };

  const pinsForSelectedPhoto = formValues.pins
    .filter((p) => p.uploadId === selectedPhotoId)
    .map((p) => {
      const status = statuses.find(
        (s) => s.id === p.vehicleDamageStatusId
      );
      return {
        id: p.tempId,
        x: p.pinX,
        y: p.pinY,
        color:
          DAMAGE_STATUS_HEX[status?.color ?? ""] ??
          DEFAULT_DAMAGE_STATUS_HEX,
        label: p.name || "Novi pin",
        statusName: status?.name ?? null,
        description: p.description,
      };
    });

  return (
    <div className="px-3 py-5 flex gap-4 h-full">
      {/* Preglednik drži lijevu stranu u punoj visini; desno su fotografije u gornjoj
          i štete u donjoj polovici. */}
      <DamagePhotoPanel
        photos={formValues.photoUploads}
        selectedPhotoId={selectedPhotoId}
        pins={pinsForSelectedPhoto}
        // Dok je modal oznake otvoren, pin se ne ističe — njegov tooltip ide u portal
        // iznad svega, pa bi visio preko modala.
        activePin={showPinModal ? null : activePin}
        onImageClick={handleImageClick}
        onPinClick={handlePinClick}
        onUploadClick={handleUploadClick}
      />

      <div className="w-2/5 flex flex-col gap-4 min-h-0">
        <DamagePhotoList
          photos={formValues.photoUploads}
          selectedPhotoId={selectedPhotoId}
          onPhotoSelect={setSelectedPhotoId}
          onPhotoDelete={handlePhotoDelete}
          onUploadClick={handleUploadClick}
        />

        <div className="flex-1 min-h-0 flex flex-col border border-slate-200 bg-white">
          <div className="flex items-center justify-between px-3 py-3 border-b border-slate-200">
            <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
              Štete ({formValues.pins.length})
            </h3>
            <p className="text-xs text-slate-500">Kliknite na fotografiju</p>
          </div>

          {/* Tablica se skrola unutar svoje polovice, da zaglavlje sekcije ostane vidljivo. */}
          <div className="flex-1 min-h-0 overflow-auto">
            {formValues.pins.length > 0 && (
              <DamagePinTable
                pins={formValues.pins}
                photos={formValues.photoUploads}
                statuses={statuses}
                activePin={activePin}
                onPinSelect={handlePinSelect}
                onPinEdit={handlePinEdit}
                onPinDelete={handlePinDelete}
              />
            )}
          </div>
        </div>
      </div>

      <FileUploadModal
        show={showUploadModal}
        onHide={handleUploadModalHide}
        onSave={handlePhotoUploadSave}
        title="Upload fotografija"
      />

      <DamagePinEditModal
        show={showPinModal}
        onHide={() => {
          setShowPinModal(false);
          setEditingPin(null);
        }}
        onSave={handlePinSave}
        pin={editingPin}
        statuses={statuses}
      />
    </div>
  );
};

export default memo(DamagePhotoPinForm);
